import { z } from "zod";
import { PublishedDocEnvironment } from "../dsm";
import { DbCreateInputOmit, DbUpdate, nullishToOptional } from "../helpers";
import { ExporterPropertyValueMap } from "./export-configuration";
import { ExportDestinationsMap } from "./export-destinations";

//
// Enums
//

export const ExportJobDestinationType = z.enum([
  "s3",
  "webhookUrl",
  "github",
  "documentation",
  "azure",
  "gitlab",
  "bitbucket",
]);
export const ExportJobStatus = z.enum(["InProgress", "Success", "Failed", "Timeout"]);
export const ExportJobLogEntryType = z.enum(["success", "info", "warning", "error", "user"]);

export type ExportJobDestinationType = z.infer<typeof ExportJobDestinationType>;
export type ExportJobStatus = z.infer<typeof ExportJobStatus>;
export type ExportJobLogEntryType = z.infer<typeof ExportJobLogEntryType>;

//
// Results
//

export const ExportJobLogEntry = z.object({
  id: z.string().optional(),
  time: z.coerce.date(),
  type: ExportJobLogEntryType,
  message: z.string(),
});

export const ExportJobPullRequestDestinationResult = z.object({
  pullRequestUrl: z.string(),
  sparseCheckoutUsed: nullishToOptional(z.boolean()),
});

export const ExportJobS3DestinationResult = z.object({
  bucket: z.string(),
  urlPrefix: z.string().optional(),
  path: z.string(),
  files: z.array(z.string()),

  url: nullishToOptional(z.string()),
  urls: nullishToOptional(z.string().array()),
});

export const ExportJobDocsDestinationResult = z.object({
  url: z.string(),
});

export const ExportJobResult = z.object({
  error: z.string().optional(),
  s3: nullishToOptional(ExportJobS3DestinationResult),
  github: nullishToOptional(ExportJobPullRequestDestinationResult),
  azure: nullishToOptional(ExportJobPullRequestDestinationResult),
  gitlab: nullishToOptional(ExportJobPullRequestDestinationResult),
  bitbucket: nullishToOptional(ExportJobPullRequestDestinationResult),
  sndocs: nullishToOptional(ExportJobDocsDestinationResult),
  logs: nullishToOptional(ExportJobLogEntry.array()),
});

export type ExportJobLogEntry = z.infer<typeof ExportJobLogEntry>;
export type ExportJobPullRequestDestinationResult = z.infer<typeof ExportJobPullRequestDestinationResult>;
export type ExportJobS3DestinationResult = z.infer<typeof ExportJobS3DestinationResult>;
export type ExportJobDocsDestinationResult = z.infer<typeof ExportJobDocsDestinationResult>;
export type ExportJobResult = z.infer<typeof ExportJobResult>;

//
// Job
//

export const ExportJob = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  finishedAt: z.coerce.date().optional(),
  designSystemId: z.string(),
  designSystemVersionId: z.string(),
  workspaceId: z.string(),
  scheduleId: z.string().nullish(),
  exporterId: z.string(),
  brandId: z.string().optional(),
  themeId: z.string().optional(),
  themePersistentIds: z.string().array().optional(),
  estimatedExecutionTime: z.number().optional(),
  status: ExportJobStatus,
  result: ExportJobResult.optional(),
  createdByUserId: z.string().optional(),
  exporterPropertyValues: ExporterPropertyValueMap.optional(),
  previewMode: z.boolean().optional(),
  exportContextId: z.string().optional().nullable(),

  // Destinations
  ...ExportDestinationsMap.shape,
});

export type ExportJob = z.infer<typeof ExportJob>;

export type CreateExportJob = DbCreateInputOmit<ExportJob>;
export type UpdateExportJob = Pick<DbUpdate<ExportJob>, "id" | "status" | "result" | "finishedAt">;

//
// Support
//

export const ExportJobFindByFilter = ExportJob.pick({
  exporterId: true,
  designSystemVersionId: true,
  createdByUserId: true,
  status: true,
  scheduleId: true,
  designSystemId: true,
  themeId: true,
  brandId: true,
})
  .extend({
    destinations: z.array(ExportJobDestinationType),
    docsEnvironment: PublishedDocEnvironment,
  })
  .partial();

export type ExportJobFindByFilter = z.infer<typeof ExportJobFindByFilter>;
